import { createContext, useContext, useMemo, useState } from 'react';

import { UIArea } from 'src/state/ui/types';
import { useNavbarUIState } from 'src/state/ui/useNavbarUIState';

type NavbarContextType = {
  hiddenNavBar: boolean;
  setHiddenNavBar(_hiddenNavBar: boolean): void;
  openNavBar: boolean;
  setOpenNavBar: (openNav: boolean) => void;
  toggleNavBar: () => void;
};

const initialState: NavbarContextType = {
  hiddenNavBar: false,
  setHiddenNavBar(_hiddenNavBar: boolean) {},
  openNavBar: true,
  setOpenNavBar: (openNav: boolean) => {},
  toggleNavBar: () => {},
};

export const NavbarContext = createContext(initialState);

export function useNavbarContext() {
  const context = useContext(NavbarContext);

  if (!context)
    throw new Error(
      'useNavbarContext must be use inside NavbarContextProvider',
    );

  return context;
}

type Props = {
  children: React.ReactNode;
  area: UIArea;
};

export function NavbarContextProvider({ children, area }: Props) {
  const { hidden, setHidden } = useNavbarUIState(area);
  const [openNav, setOpenNav] = useState(false);

  const memoizedValue = useMemo(
    () => ({
      area,
      hiddenNavBar: hidden,
      openNavBar: openNav,
      setHiddenNavBar: (hide: boolean) => setHidden(hide),
      setOpenNavBar: (openNav: boolean) => setOpenNav(openNav),
      toggleNavBar: () => setOpenNav(!openNav),
    }),
    [area, hidden, setHidden, openNav],
  );

  return (
    <NavbarContext.Provider value={memoizedValue}>
      {children}
    </NavbarContext.Provider>
  );
}
